//Imports
import React, {
    useEffect,
    useRef,
    useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination
} from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import { Grid } from "@mui/material";

//Actions
import { sendGenericExport } from "Redux/actions";
import { manageSearchAssetSubtypes, reset } from './redux/slice';
import { clearRedux } from 'Redux/actions';
import { openModalCreateEditAssetSubtype } from './modals/createEditAssetSubtype/redux/slice';
import { openModalDeleteAssetSubtype } from './modals/deleteAssetSubtype/redux/slice';

//Views
import CreateEditManageAssetSubtype from './modals/createEditAssetSubtype';
import DeleteManageAssetSubtype from './modals/deleteAssetSubtype';

const AssetSubtype = (props) => {

    //Table refs
    const assetSubtypesTableRef = useRef();

    const user = useSelector(state => state.securityRedux.user);
    const openModalCreateEdit = useSelector(state => state.manageCreateEditAssetSubtypeRedux.open);
    const openModalDelete = useSelector((state) => state.manageDeleteManageAssetSubtypeRedux.open);

    // Permissions
    const editablePermissions = HasPermission(user, "", [341])

    // Hooks
    const dispatch = useDispatch();

    //Language
    const { messages, locale } = useIntl();

    const paginationAssetSubtype = useTablePagination({
        dispatch,
        action: manageSearchAssetSubtypes,
        searchFields: ["name"],
        tableRef: assetSubtypesTableRef,
        sendGenericExport,
        exportName: "asset-subtypes",
        termsFields: [{
            fields: "companyId",
            terms: [user?.companyId],
            exact: true
        }]
    });

    //Local States
    const [selectedItems, setSelectedItems] = useState([]);

    //Redux States
    const { loading, items, total } = useSelector((state) => state.manageAssetSubtypesTabRedux);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);

    useEffect(() => {
        paginationAssetSubtype.setColumns([
            {
                key: "name",
                title: messages["manage_createEditAssetSubtype_name"],
                functionString: 'return row?.name',
                show: true,
                export: true,
                sort: true,
                width: 700,
                exportValue: (row) => row?.name,
                render: (row) => (
                    <div style={{ width: 350, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ wordWrap: 'break-word', width: 300 }}>
                            <p>{row.name}</p>
                        </div>
                        {editablePermissions &&
                            <div>
                                <MenuComponent
                                    placement="right-start"
                                    options={[messages['edit'], messages['delete']]}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={(value) => {
                                        if (value === messages['edit']) {
                                            dispatch(openModalCreateEditAssetSubtype(row?.id));
                                        } else if (value === messages['delete']) {
                                            dispatch(openModalDeleteAssetSubtype({ assetSubtype: row }))
                                        }
                                    }}
                                    value={row}
                                />
                            </div>
                        }
                    </div>
                ),
            }
        ]);
        paginationAssetSubtype.onChangeSort({ key: "name", order: "asc" });
        return () => dispatch(reset());
    }, [locale]);

    useEffect(() => {
        if (!exportResult || exportName !== "asset-subtypes") return;

        NotificationManager.info(
            messages["exportInProcessBody"],
            messages["exportInProcessTitle"],
            4000,
            null,
            null,
            "info",
            null
        );
        dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }, [exportResult, exportName]);

    const refreshTable = () => {
        setSelectedItems([]);
        assetSubtypesTableRef?.current?.setPage(1);
        assetSubtypesTableRef?.current?.setSelectedItems([]);
        paginationAssetSubtype.onChangePage(1);
    };

    return (
        <div>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            {editablePermissions &&
                                <IconButton
                                    tooltip={messages["add"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="add_box"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => {
                                        dispatch(openModalCreateEditAssetSubtype())
                                    }}
                                />
                            }
                            {
                                editablePermissions && selectedItems?.length ?
                                    <IconButton
                                        tooltip={messages["delete"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="delete"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openModalDeleteAssetSubtype({ assetSubtypeList: selectedItems }))
                                        }}
                                        style={{ opacity: !selectedItems.length && "0.6" }}
                                        isDisabled={!selectedItems.length}
                                    /> : <></>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationAssetSubtype}
                                buttons={["export"]}
                                onExport={(type) => {
                                    paginationAssetSubtype.onExport(type);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["manage_createEditAssetSubtype_searchTable"]}
                        searchText={paginationAssetSubtype.searchText}
                        onSearchText={paginationAssetSubtype.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                keyColumn="id"
                loading={loading}
                topheight={235}
                data={items}
                total={total}
                exportOrientation="landscape"
                onSelectedItems={(items) => setSelectedItems(items)}
                {...paginationAssetSubtype}
                hideDragable
                hideAllInPagination
            />
            {openModalCreateEdit && <CreateEditManageAssetSubtype refresh={refreshTable} />}
            {openModalDelete && <DeleteManageAssetSubtype refresh={refreshTable} />}
        </div>
    )

}

export default AssetSubtype;